import React from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Button, ButtonToolbar } from 'rsuite';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { Field } from './common/Field'
import AuthLayout, { authCardClass, focusFirstError } from './common/AuthLayout';
import { toast } from 'react-toastify';
import { Validator } from "@rsuite/validation";
import { scrollToTop } from '../utils/scrollUtils';

const apiUrl = process.env.REACT_APP_SERVER_API_URL;

const model = Validator.SchemaModel({
  firstName: "required",
  lastName: "required",
  email: "required|email",
  phone: "required",
  postalCode: "required",//"required|regex:\\d{6}",
  address: "required",
  password: "required|min:6|regex:[A-Z]|regex:[a-z]|regex:[0-9]",
}, {
  "password.regex": "Should contain at least one number and have a mixture of uppercase and lowercase letters"
});

// All fields are required by the validation model above
const required = (label) => <>{label} <span style={{ color: 'red' }}>*</span></>;

const Register = () => {

  const [formValue, setFormValue] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    postalCode: '',
    address: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const updateFormValue = (fv) => {
    // if (fv.postalCode !== formValue.postalCode && fv.postalCode.length === 6) {
      // searchAddressByPostalcode(fv, setFormValue);
    // } else {
      setFormValue(fv);
    // }
  }

  const navigate = useNavigate();

  const formRef = React.useRef();

  const register = (e) => {
    if (!formRef.current.check()) {
      focusFirstError();
      return;
    }
    setIsSubmitting(true);
    const newUser = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      phone: formValue.phone,
      postalCode: formValue.postalCode,
      address: formValue.address,
      password: formValue.password
    };

    axios.post(`${apiUrl}/users`, newUser)
      .then(() => {

        axios.post(`${apiUrl}/users/login`, {
          email: formValue.email, password: formValue.password
        }).then((loginResponse) => {
          localStorage.setItem("auth-token", loginResponse.data.token);
          localStorage.setItem("user-id", loginResponse.data.user.id);
          navigate("/");
          scrollToTop();
        });

      })
      .catch((error) => {
        setIsSubmitting(false);
        toast.error(error.response.data.msg || 'Oops! Something went wrong. We\'re looking into it.', { position: "top-center" });
      });
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      register();
    }
  }


  return (

    <AuthLayout>
      <Form
        ref={formRef}
        onChange={fv => updateFormValue(fv)}
        formValue={formValue}
        model={model}
        className={authCardClass('wide') + ' auth-form mb-6'}
        size="full"
        onKeyDown={handleKeyDown}
      >
        <h1 className="text-xl font-bold text-gray-900 mb-1">Create your account</h1>
        <p className="text-sm text-gray-600 mb-4">Register to book Newtonshow camps for your kids.</p>

        {/* Your details */}
        <p className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-1 mb-3">Your details</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-4">
          <Field
            name="firstName"
            label={required('First name')}
            autoFocus
            autoComplete="given-name"
          />
          <Field
            name="lastName"
            label={required('Last name')}
            autoComplete="family-name"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-4">
          <Field
            name="email"
            label={required('Email')}
            type="email"
            autoComplete="email"
            autoCapitalize="off"
          />
          <Field
            name="phone"
            label={required('Mobile number')}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
          />
        </div>

        {/* Contact details */}
        <p className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-1 mb-3 mt-1">Contact details</p>
        {/* Short postal code gets 1/4, address 3/4 */}
        <div className="grid grid-cols-1 sm:grid-cols-4 sm:gap-x-4">
          <Field
            name="postalCode"
            label={required('Postal code')}
            inputMode="numeric"
            autoComplete="postal-code"
          />
          <div className="sm:col-span-3">
            <Field
              name="address"
              label={required('Address')}
              autoComplete="street-address"
            />
          </div>
        </div>

        {/* Security */}
        <p className="text-sm font-semibold text-gray-900 border-b border-gray-100 pb-1 mb-3 mt-1">Security</p>
        <div className="relative">
          <Field
            name="password"
            label={required('Password')}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            style={{ paddingRight: 40 }}
          />
          <button
            type="button"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            title={showPassword ? 'Hide password' : 'Show password'}
            className="absolute right-3 text-gray-400 hover:text-gray-600 bg-transparent border-0 cursor-pointer p-1"
            style={{ top: 34 }}
            onClick={() => setShowPassword(v => !v)}
          >
            {showPassword ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
          </button>
        </div>
        <p className="text-xs text-gray-500 -mt-3 mb-4">
          At least 6 characters, with uppercase and lowercase letters and a number.
        </p>

        <p className="text-xs text-gray-600 mb-3">
          By signing up, you agree to the{' '}
          <a
            className="text-blue-600 hover:text-blue-800 underline"
            href="https://www.newtonshowcamp.com/terms-and-conditions"
            target="_blank"
            rel="noreferrer"
          >
            Terms and Conditions
          </a>
        </p>

        <ButtonToolbar>
          <Button
            appearance="primary"
            onClick={register}
            block
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Sign Up
          </Button>
          <div className="mt-3 block w-full text-sm text-center text-gray-600">
            Already have an account?{' '}
            <Link to="../login" className="no-underline text-blue-600 hover:text-blue-800 font-medium">Log in</Link>
          </div>
        </ButtonToolbar>

      </Form>
    </AuthLayout>
  )
}

export default Register;
